/**
 * Low-level HTTP helpers used by the transport.
 *
 * `fetchAuthed` is the single funnel for cloud API requests. Auth is
 * cookie-based: every call sends `credentials: 'include'` so the HttpOnly
 * `occurra_token` cookie set by /vms/api/v1/auth/session rides along.
 *
 * A 401 is handled the same for every cloud API call: `sessionRefresher` refreshes the token and
 * the request is sent once more. When the session ended, `sessionExpiry` signs the page out
 * (see session.ts).
 */

import type { z } from 'zod';

import { authSession } from '@/auth/authSession';
import { SECOND_MS } from '@/lib/timeUnits';
import type { AuditListFilter } from '@/types/audit';
import { RefreshOutcome } from '@/types/auth';
import type { BookmarkListFilter } from '@/types/bookmarks';
import type { AnswerOrigin, Boundary, RowsRequest, ViolationSource } from '@/types/contract';
import type {
    ClipDownloadOpts, FramesOpts, ImageOpts, ImageQuality,
} from '@/types/devices';
import type { HealthLogFilter } from '@/types/healthMessages';
import type { HeatmapRecordsOpts, MetadataSegmentsOpts } from '@/types/heatmap';

import { ApiError } from './apiError';
import { getAnswerOrigin, JsonTextSchema, parseOne, parseRows } from './contract';
import { contractReporter } from './contractReporter';
import { fetchWithCookie, httpError, REQUEST_TIMEOUT_MS } from './http';
import { HTTP_STATUS } from './httpStatus';
import { portalMonitor } from './portalMonitor';
import { sessionExpiry, sessionRefresher } from './session';

export { ApiError };

/** A case attachment may be 100 MB, and the link may be a phone's. */
const UPLOAD_TIMEOUT_MS = 10 * 60 * SECOND_MS;

/**
 * What a write that got no answer says. The browser says "Failed to fetch" for every such
 * request, and a toast for a failed save would then name a read.
 */
const UNANSWERED_WRITE: Readonly<Record<string, string>> = {
    POST: 'Failed to create',
    PATCH: 'Failed to update',
};

function toUnansweredError(error: unknown, method = 'GET'): unknown {
    const message = UNANSWERED_WRITE[method.toUpperCase()];
    // Only a network failure is renamed: a caller tells its own cancel by the error's name.
    if (!(error instanceof TypeError) || !message) {
        return error;
    }
    return new TypeError(message, { cause: error });
}

async function fetchMonitored(url: string, init: RequestInit): Promise<Response> {
    let resp: Response;
    try {
        resp = await fetchWithCookie(url, init);
    } catch (error) {
        portalMonitor.reportError(url, error);
        throw toUnansweredError(error, init.method);
    }
    portalMonitor.reportResponse(resp.status);
    return resp;
}

export async function fetchAuthed(url: string, init: RequestInit = {}): Promise<Response> {
    let resp = await fetchMonitored(url, init);
    // Only refresh or redirect when the page was signed in (`authSession.wasSignedIn`): a boot
    // probe with no prior sign-in must not redirect on its own 401.
    if (resp.status !== HTTP_STATUS.UNAUTHORIZED || !authSession.wasSignedIn()) {
        return resp;
    }
    const outcome = await sessionRefresher.refresh();
    if (outcome === RefreshOutcome.Refreshed) {
        resp = await fetchMonitored(url, init);
    }
    // A network failure of the refresh keeps the session: the next request tries again.
    if (outcome === RefreshOutcome.Ended ||
        (outcome === RefreshOutcome.Refreshed && resp.status === HTTP_STATUS.UNAUTHORIZED)) {
        sessionExpiry.handle();
    }
    return resp;
}

interface Answer {
    rawValue: unknown;
    origin: AnswerOrigin;
}

/** How a boundary sends its requests. */
type Fetcher = (url: string, init: RequestInit) => Promise<Response>;

/**
 * The parsed helpers, bound to the source of the answers they read, which the transport knows
 * when it builds the request: the cloud, or the media_server a relay request goes through.
 * `send` makes the requests; the default sends a signed-out page to the login
 * screen on a 401. See docs/rules/api_boundary.md.
 */
export function createBoundary(source: ViolationSource, send: Fetcher = fetchAuthed): Boundary {
    /** A failed request stays an `ApiError` with the server's reason; a 2xx answer is JSON. */
    async function readAnswer(url: string, resp: Response): Promise<Answer> {
        if (!resp.ok) {
            throw await httpError(resp);
        }
        const origin = getAnswerOrigin(url, resp.status, source);
        if (resp.status === HTTP_STATUS.NO_CONTENT) {
            return { rawValue: null, origin };
        }
        return { rawValue: parseOne(JsonTextSchema, await resp.text(), origin), origin };
    }

    async function readParsed<S extends z.ZodType>(
        url: string, schema: S, init: RequestInit,
    ): Promise<z.output<S>> {
        const { rawValue, origin } = await readAnswer(url, await send(url, init));
        return parseOne(schema, rawValue, origin);
    }

    function sendParsed<S extends z.ZodType>(
        method: string, url: string, body: unknown, schema: S, init: RequestInit = {},
    ): Promise<z.output<S>> {
        const headers = new Headers(init.headers);
        headers.set('Content-Type', 'application/json');
        return readParsed(url, schema, {
            ...init,
            method,
            headers,
            body: JSON.stringify(body),
        });
    }

    return {
        fetchParsed<S extends z.ZodType>(
            url: string, schema: S, init: RequestInit = {},
        ): Promise<z.output<S>> {
            return readParsed(url, schema, init);
        },
        /** A list keeps the rows that parse; `getKey` names a row in a report. */
        async fetchRows<S extends z.ZodType>(
            url: string, itemSchema: S, { init = {}, getKey }: RowsRequest = {},
        ): Promise<z.output<S>[]> {
            const { rawValue, origin } = await readAnswer(url, await send(url, init));
            return parseRows(itemSchema, rawValue, origin, contractReporter, getKey);
        },
        postParsed<S extends z.ZodType>(
            url: string, body: unknown, schema: S, init: RequestInit = {},
        ): Promise<z.output<S>> {
            return sendParsed('POST', url, body, schema, init);
        },
        putParsed<S extends z.ZodType>(
            url: string, body: unknown, schema: S,
        ): Promise<z.output<S>> {
            return sendParsed('PUT', url, body, schema);
        },
        patchParsed<S extends z.ZodType>(
            url: string, body: unknown, schema: S,
        ): Promise<z.output<S>> {
            return sendParsed('PATCH', url, body, schema);
        },
        deleteParsed<S extends z.ZodType>(url: string, schema: S): Promise<z.output<S>> {
            return readParsed(url, schema, { method: 'DELETE' });
        },
        /** Posts a multipart form, which the browser types itself; the timeout is an upload's. */
        postFormParsed<S extends z.ZodType>(
            url: string, form: FormData, schema: S,
        ): Promise<z.output<S>> {
            return readParsed(url, schema, {
                method: 'POST',
                body: form,
                signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
            });
        },
    };
}

/** Total size of the underlying resource, whether the answer was 200 or 206. */
function getTotalBytes(resp: Response): number {
    // "bytes <start>-<end>/<total>" — the only place a 206 states the whole size.
    const range = resp.headers.get('Content-Range');
    const total = range ? Number(range.split('/')[1]) : 0;
    if (Number.isFinite(total) && total > 0) {
        return total;
    }
    const length = Number(resp.headers.get('Content-Length'));
    return Number.isFinite(length) ? length : 0;
}

interface FetchTextOpts {
    /** Sent verbatim as the Range header. */
    range?: string;
    onProgress?: (receivedB: number, totalB: number) => void;
    signal?: AbortSignal;
}

interface TextTransfer {
    text: string;
    partial: boolean;
    totalB: number;
}

/**
 * Reads a text resource, reporting progress as the bytes arrive and honoring
 * cancellation. Passing `signal` also lifts the module's default deadline,
 * which a multi-megabyte transfer over a slow relay would otherwise trip.
 *
 * `range` is sent verbatim as the Range header. A server that ignores it
 * answers 200, which is why `partial` follows the status rather than the ask.
 */
export async function fetchText(
    url: string, { range, onProgress, signal }: FetchTextOpts = {},
): Promise<TextTransfer> {
    const headers = range ? { Range: range } : undefined;
    const resp = await fetchAuthed(url, { headers, signal });
    if (!resp.ok) {
        throw await httpError(resp);
    }

    const totalB = getTotalBytes(resp);
    const partial = resp.status === HTTP_STATUS.PARTIAL_CONTENT;

    // A body without a reader (jsdom, a mocked fetch) still has to work; it just
    // reports no intermediate progress.
    if (!resp.body?.getReader) {
        const text = await resp.text();
        onProgress?.(text.length, totalB || text.length);
        return { text, partial, totalB: totalB || text.length };
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    const chunks: string[] = [];
    let receivedB = 0;
    try {
        for (;;) {
            // eslint-disable-next-line no-await-in-loop -- a stream yields its chunks in order
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            receivedB += value.byteLength;
            chunks.push(decoder.decode(value, { stream: true }));
            onProgress?.(receivedB, totalB);
        }
    } finally {
    // Abort mid-transfer must not leave the relay session held open; the
    // stream is only released back when the reader lets go of it.
        reader.releaseLock();
    }
    chunks.push(decoder.decode());
    return { text: chunks.join(''), partial, totalB: totalB || receivedB };
}

/**
 * Reads a binary resource, or null for a 204, which says that the server has nothing to give.
 * The request ends at the caller's signal or at the default deadline.
 */
export async function fetchBlob(
    url: string, { signal }: { signal: AbortSignal },
): Promise<Blob | null> {
    // A signal replaces the default deadline of fetchAuthed, so the deadline joins it here.
    const resp = await fetchAuthed(url, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)]),
    });
    if (!resp.ok) {
        throw await httpError(resp);
    }
    if (resp.status === HTTP_STATUS.NO_CONTENT) {
        return null;
    }
    return await resp.blob();
}

/**
 * POSTs a request whose answer carries nothing to read: 202 for work the server queued, 204 for
 * work it did. Any other success is a server that does not know the route - a media_server serves
 * its web app for a path it has no handler for - so it fails as a server too old for the request.
 */
export async function postAction(url: string, body: unknown = {}): Promise<void> {
    const resp = await fetchAuthed(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!resp.ok) {
        throw await httpError(resp);
    }
    if (resp.status !== HTTP_STATUS.ACCEPTED && resp.status !== HTTP_STATUS.NO_CONTENT) {
        throw new ApiError('the server needs an update to do this', resp.status, url);
    }
}

export function esc(text: string): string {
    const span = document.createElement('span');
    span.textContent = text;
    return span.innerHTML;
}

/**
 * Build the query string for `GET /api/v1/devices/{id}/image`.
 *
 * `quality`: 'low' | 'high' (stream selector — high-res primary vs sub-stream).
 * `opts.position`: UTC milliseconds — when set, fetch an archive frame at that
 *     instant. Omitted → media_server returns the latest live frame.
 * `opts.exact`: 'roundLeft' | 'exact' | 'roundRight' — rounding mode for
 *     `position`. Ignored when no position is set.
 * `opts.crop`: normalized [0..1] rect `{x, y, width, height}`. When set,
 *     appended as `crop=x1,y1,x2,y2`. Skipped for a zero-area / undefined
 *     rect.
 * `opts.width` / `opts.height`: target pixel size — media_server resizes the
 *     output frame. Set one (or both) to fetch a lightweight thumbnail
 *     instead of the full-resolution stream frame.
 */
export function buildImageQuery(quality?: ImageQuality, opts: ImageOpts = {}): string {
    const params = new URLSearchParams();
    if (quality) {
        params.set('quality', quality);
    }
    if (opts.position !== undefined) {
        params.set('position', String(opts.position));
    }
    if (opts.exact) {
        params.set('exact', opts.exact);
    }
    if (opts.width) {
        params.set('width', String(opts.width));
    }
    if (opts.height) {
        params.set('height', String(opts.height));
    }
    if (opts.crop && opts.crop.width > 0 && opts.crop.height > 0) {
        const { crop } = opts;
        const x2 = crop.x + crop.width;
        const y2 = crop.y + crop.height;
        params.set('crop', `${crop.x},${crop.y},${x2},${y2}`);
    }
    const query = params.toString();
    return query ? `?${query}` : '';
}

/**
 * Path of `GET /api/v1/streaming/download/{id}` in export mode: the media_server
 * answers with an MP4 attachment, so navigating to it downloads a file.
 */
export function buildClipDownloadPath(cameraId: string, opts: ClipDownloadOpts): string {
    const params = new URLSearchParams({
        startTimeMs: String(Math.round(opts.startMs)),
        endTimeMs: String(Math.round(opts.endMs)),
        mode: 'export',
    });
    if (opts.quality) {
        params.set('quality', opts.quality);
    }
    return `/api/v1/streaming/download/${encodeURIComponent(cameraId)}?${params}`;
}

/**
 * Build the query string for `GET /api/v1/devices/{id}/frames` — a batch of
 * archive snapshots sampled across a time range in one request (replaces N
 * separate `image?exact=exact` round trips).
 *
 * `opts.startTimeMs` / `opts.endTimeMs`: required range, UTC ms.
 * `opts.sampleIntervalMs`: required spacing between samples, ms.
 * `opts.quality`: 'primary' (high-res) or 'secondary' (low-res sub-stream).
 * `opts.width`: target pixel width (server resizes to a thumbnail).
 * `opts.crop`: normalized [0..1] rect `{x, y, width, height}`, as buildImageQuery.
 */
export function buildFramesQuery(opts: FramesOpts): string {
    const params = new URLSearchParams();
    params.set('startTimeMs', String(Math.round(opts.startTimeMs)));
    params.set('endTimeMs', String(Math.round(opts.endTimeMs)));
    params.set('sampleIntervalMs', String(Math.round(opts.sampleIntervalMs)));
    if (opts.quality) {
        params.set('quality', opts.quality);
    }
    if (opts.width) {
        params.set('width', String(opts.width));
    }
    if (opts.crop && opts.crop.width > 0 && opts.crop.height > 0) {
        const { crop } = opts;
        params.set('crop', `${crop.x},${crop.y},${crop.x + crop.width},${crop.y + crop.height}`);
    }
    return `?${params}`;
}

/** A segments query; `flat=false` asks for `{periods}`, the shape the schema reads. */
export function getSegmentsQuery(opts: MetadataSegmentsOpts): string {
    const params = new URLSearchParams();
    params.set('category', opts.category);
    params.set('flat', 'false');
    params.set('interval', String(opts.intervalMs));
    if (opts.utcTimeMs !== undefined) {
        params.set('utcTimeMs', String(opts.utcTimeMs));
    }
    if (opts.utcEndTimeMs !== undefined) {
        params.set('utcEndTimeMs', String(opts.utcEndTimeMs));
    }
    if (opts.boundingBox !== undefined) {
        params.set('boundingBox', opts.boundingBox);
    }
    return params.toString();
}

export function getHeatmapRecordsQuery(opts: HeatmapRecordsOpts): string {
    const params = new URLSearchParams();
    params.set('category', opts.category);
    params.set('utcTimeMs', String(opts.utcTimeMs));
    params.set('utcEndTimeMs', String(opts.utcEndTimeMs));
    params.set('limit', String(opts.limit));
    return params.toString();
}

/**
 * The query of `GET /audit`: every bound and set the trail narrows by. The
 * values are named first, as the wire spells them, and an absent one is left
 * out; the params come from what is left.
 */
/** The epoch ms as the log endpoints take a bound, else nothing. */
function getIso(ms?: number): string | undefined {
    return ms === undefined ? undefined : new Date(ms).toISOString();
}

/**
 * The query of the parameters that are set: a value absent or empty names nothing, and a
 * parameter given as a list is repeated once per member.
 */
export function queryOf(
    values: Record<string, string | number | undefined>,
    lists: Record<string, readonly string[] | undefined> = {},
): string {
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(values)) {
        if (value !== undefined && value !== '') {
            params.set(name, String(value));
        }
    }
    for (const [name, members] of Object.entries(lists)) {
        for (const member of members ?? []) {
            params.append(name, member);
        }
    }
    return params.toString();
}

export function buildHealthLogQuery(filter: HealthLogFilter = {}): string {
    return queryOf({
        before: filter.beforeSeq,
        after: filter.afterSeq,
        limit: filter.limit,
        from: getIso(filter.fromMs),
        to: getIso(filter.toMs),
        severity: filter.severity,
        serverId: filter.serverId,
    }, { type: filter.types });
}

export function buildAuditQuery(filter: AuditListFilter = {}): string {
    const list = (items?: string[]): string | undefined => (
        items?.length ? items.join(',') : undefined);
    return queryOf({
        before: filter.beforeSeq,
        limit: filter.limit,
        from: getIso(filter.fromMs),
        to: getIso(filter.toMs),
        actor: filter.actor,
        session: filter.sessionId,
        types: list(filter.types),
        devices: list(filter.deviceIds),
        resource: filter.resourceId,
    });
}

/**
 * Build the query fragment for `GET /bookmarks`.
 * Returns a bare `key=value&...` string — empty when nothing narrows — so the
 * caller decides how to splice it into its own URL shape.
 */
export function buildBookmarksQuery(filter: BookmarkListFilter = {}): string {
    const params = new URLSearchParams();
    if (filter.deviceId) {
        params.set('deviceId', filter.deviceId);
    }
    if (filter.fromMs !== undefined) {
        params.set('fromMs', String(filter.fromMs));
    }
    if (filter.toMs !== undefined) {
        params.set('toMs', String(filter.toMs));
    }
    for (const tag of filter.tags ?? []) {
        params.append('tag', tag);
    }
    if (filter.text) {
        params.set('text', filter.text);
    }
    if (filter.limit !== undefined) {
        params.set('limit', String(filter.limit));
    }
    return params.toString();
}
